import { useEffect, useRef, useState } from 'react';
import { useGetCheckoutStatusQuery } from '../app/services/api';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { PaymentStatus } from '../components/common/layout/PaymentStatusCard';

interface UsePaymentStatusPollingProps {
    checkoutId: string | undefined;
    isPolling: boolean;
    timeout?: number;
}

export const usePaymentStatusPolling = ({
    checkoutId,
    isPolling,
    timeout = 60000,
}: UsePaymentStatusPollingProps) => {
    const timeoutRef = useRef<NodeJS.Timeout>();
    const [paymentStatus, setPaymentStatus] =
        useState<PaymentStatus>('processing');
    const [hasTimedOut, setHasTimedOut] = useState(false);

    const { data } = useGetCheckoutStatusQuery(
        isPolling && !hasTimedOut ? checkoutId ?? skipToken : skipToken,
        {
            pollingInterval: isPolling && !hasTimedOut ? 3000 : 0,
            skip: !isPolling || hasTimedOut,
        },
    );

    useEffect(() => {
        if (!isPolling) {
            // Clear timeout when polling stops and reset status
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = undefined;
            }
            setPaymentStatus('processing');
            setHasTimedOut(false);
            return;
        }

        if (timeoutRef.current === undefined) {
            timeoutRef.current = setTimeout(() => {
                setPaymentStatus('denied');
                setHasTimedOut(true);
            }, timeout);
        } else if (data?.content.completed) {
            if (data.content.success === true) {
                clearTimeout(timeoutRef.current);
                setPaymentStatus('success');
            } else if (data.content.success === false) {
                clearTimeout(timeoutRef.current);
                setPaymentStatus('denied');
            }
        }
    }, [
        data,
        isPolling,
        timeout,
    ]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return { data, paymentStatus };
};
